import React from "react";
import { FleetDefaults } from "@app/shared/src/models";
import { Card, Segmented, Spinner } from "@app/shared/src/components_v2";
import { useFleetDefaults } from "../../hooks/useFleetDefaults";
import { useFleetDefaultsRecord } from "../../hooks/useFleetDefaultsRecord";
import { useFullDevices } from "../../hooks/useFullDevices";
import { urlEnum, useUrlState } from "../../hooks/useUrlState";
import { useFleet } from "../providers/FleetProvider";
import { AlertDefaultsSection } from "../sections/AlertDefaultsSection";
import { DriverAccessSection } from "../sections/DriverAccessSection";
import { FleetNotificationsSection } from "../sections/FleetNotificationsSection";
import { FleetOverridesSection } from "../sections/FleetOverridesSection";
import { FleetRadarVideoSection } from "../sections/FleetRadarVideoSection";
import { PageTitle } from "../shared/PageTitle";

/**
 * What the fleet is meant to be running, in the design's four sections.
 *
 * Each section edits the organization's stored record rather than inferring a default from whatever
 * the trucks happen to hold: inference reads "Mixed" the moment one unit drifts, which is exactly
 * when a director most needs to know the intended value. Saving records the intent and then pushes
 * it onto the units that were following the *old* value, so a truck someone moved deliberately keeps
 * its setting — and each section confirms how many units it will touch before writing anything.
 *
 * The drift table sits under every tab, because "where is my fleet not standard?" is the question
 * this page exists to answer.
 */

type TabKey = "alerts" | "video" | "notify" | "driver";

const TAB_CODEC = urlEnum<TabKey>(["alerts", "video", "notify", "driver"]);

const TABS: { key: TabKey; label: string }[] = [
    { key: "alerts", label: "Alerts & Relays" },
    { key: "video", label: "Radar & video" },
    { key: "notify", label: "Notifications" },
    { key: "driver", label: "Driver access" },
];

export const SettingsPage: React.FC = () => {
    const [tab, setTab] = useUrlState<TabKey>("tab", "alerts", TAB_CODEC);
    const fleet = useFleetDefaults();
    // The group scope reaches here too, and this page writes: naming it is what stops "apply to
    // fleet" from meaning something different than the reader thinks.
    const { group, statsDevices } = useFleet();
    const defaultsRecord = useFleetDefaultsRecord();
    // One fan-out of complete device records for the whole page: the radar tab's push targets and
    // the drift table's radar column both need the sensor config the light roster omits.
    const full = useFullDevices(fleet.devices);

    /**
     * Record the stated default, shared by every fleet-level section.
     *
     * The record is edited in place rather than rebuilt from a spread: the generated client expects
     * Date objects on it, and a spread would hand it strings.
     */
    const saveDefaults = async (apply: (target: FleetDefaults) => void) => {
        const record = defaultsRecord.defaults;
        if (!record) return;
        apply(record);
        await defaultsRecord.save(record);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="mb-1">
                <h1 className="text-[26px] font-bold tracking-tight">
                    <PageTitle>Fleet defaults</PageTitle>
                </h1>
                <p className="mt-1.5 max-w-[72ch] text-[13.5px] leading-relaxed text-[var(--muted)]">
                    Every device {group ? `in ${group.name} ` : ""}follows these. Changing one writes it to each unit that was on
                    the shared value — units someone set differently keep their own.
                </p>
            </div>

            <Segmented full tone="accent" options={TABS} value={tab} onChange={setTab} />

            {fleet.error && <Card className="px-5 py-4 text-[13px] text-[var(--crit)]">{fleet.error}</Card>}
            {defaultsRecord.error && (
                <Card className="px-5 py-4 text-[13px] text-[var(--crit)]">{defaultsRecord.error}</Card>
            )}

            {fleet.isLoading ? (
                <Card>
                    <div className="grid place-items-center p-12 text-[var(--muted)]">
                        <Spinner />
                    </div>
                </Card>
            ) : (
                <>
                    {tab === "alerts" && <AlertDefaultsSection fleet={fleet} />}

                    {tab === "video" && (
                        <FleetRadarVideoSection
                            defaults={defaultsRecord.defaults}
                            devices={fleet.devices}
                            fullDevices={full.fullDevices}
                            unreachable={full.unreachable}
                            reloadFullDevices={full.reload}
                            chirpConfigs={fleet.chirpConfigs}
                            trackerConfigs={fleet.trackerConfigs}
                            onSave={saveDefaults}
                            onPushed={fleet.reload}
                        />
                    )}

                    {tab === "notify" && (
                        <FleetNotificationsSection
                            defaults={defaultsRecord.defaults}
                            triggerGroups={fleet.triggerGroups}
                            emailEndpoints={fleet.emailEndpoints}
                            relayEndpoints={fleet.relayEndpoints}
                            onSave={saveDefaults}
                            onPushed={fleet.reload}
                        />
                    )}

                    {tab === "driver" && <DriverAccessSection defaults={defaultsRecord.defaults} onSave={saveDefaults} />}

                    <FleetOverridesSection
                        devices={statsDevices}
                        fullDevices={full.fullDevices}
                        triggersByFacility={fleet.triggersByFacility}
                        defaults={defaultsRecord.defaults}
                        isLoading={fleet.isLoading}
                    />
                </>
            )}
        </div>
    );
};
